import { AlertService } from './alert.service';
import { AngularFirestore } from '@angular/fire/firestore';
import { Storage } from '@ionic/storage';
import { Router } from '@angular/router';
import { DbService } from './db.service';
import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/auth';
import { Observable, of } from 'rxjs';
import { switchMap, take, map, first, takeLast, last } from 'rxjs/operators';
import { Platform, LoadingController } from '@ionic/angular';
import { auth } from 'firebase/app';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  user$: Observable<any>;

  constructor(
    private afAuth: AngularFireAuth,
    private db: DbService,
    private afs: AngularFirestore,
    private router: Router,
    private storage: Storage,
    private platform: Platform,
    public loadingController: LoadingController,
    private alertService: AlertService
  ) {
    this.user$ = this.afAuth.authState.pipe(
      switchMap((user) => {
        if (user) {
          return this.afs.doc(`users/${user.uid}`).valueChanges();
        } else {
          return of(null);
        }
      })
    );
  }

  // Get the user id from firebase auth promise

  uid() {
    return this.user$
      .pipe(
        take(1),
        map((u) => u && u.uid)
      )
      .toPromise();
  }

  async getUser() {
    return await this.user$.pipe(first()).toPromise();
  }

  async anonymousLogin() {
    const credential = await this.afAuth.auth.signInAnonymously();

    return await this.updateUserData(credential.user);
  }

  async registerUser(credentials) {
    return this.afAuth.auth
      .createUserWithEmailAndPassword(credentials.email, credentials.password)
      .then((data) => {
        this.updateUserData({
          uid: data.user.uid,
          email: credentials.email,
          displayName: credentials.displayName,
          photoURL: '',
          isAnonymous: false,
        });
        return { success: true, data: data.user.uid };
      })
      .catch((err) => {
        return { success: false, data: err };
      });
  }

  async signInWithEmail({ email, password }) {
    return this.afAuth.auth.signInWithEmailAndPassword(email, password);
  }

  private updateUserData({ uid, email, displayName, photoURL, isAnonymous }) {
    const path = `users/${uid}`;

    const data = {
      uid,
      email,
      displayName,
      photoURL,
      isAnonymous,
    };

    return this.db.updateAt(path, data);
  }

  async signOut() {
    await this.afAuth.auth.signOut().then(() => {
      this.router.navigateByUrl('/');
    });
  }

  setRedirect(val) {
    this.storage.set('authRedirect', val);
  }

  async isRediect() {
    return await this.storage.get('authRedirect');
  }

  async googleLogin() {
    try {
      const provider = new auth.GoogleAuthProvider();
      return this.afAuth.auth
        .signInWithPopup(provider)
        .then((credential) => {
          this.updateUserData(credential.user);
        })
        .catch((err) => {
          console.log(err);
        });
    } catch (error) {
      console.log(error);
      this.alertService.presentAlert({
        header: 'Login Error',
        message: error.message,
        buttons: ['OK'],
      });
    }
  }

  async facebookLogin() {
    try {
      const provider = new auth.FacebookAuthProvider();
      return this.afAuth.auth.signInWithPopup(provider).then((credential) => {
        console.log(credential);
        this.updateUserData(credential.user);
      });
    } catch (error) {
      console.log(error);
      this.alertService.presentAlert({
        header: 'Login Error',
        message: error.message,
        buttons: ['OK'],
      });
    }
  }

  private async handleRedirect() {
    if ((await this.isRediect()) !== true) {
      return null;
    }

    const loading = await this.loadingController.create();
    await loading.present();

    const result = await this.afAuth.auth.getRedirectResult();

    if (result.user) {
      await this.updateUserData(result.user);
    }
    await loading.dismiss();
    await this.setRedirect(false);
    return result;
  }

  // async nativeGoogleLogin(): Promise<any> {
  //   const gplusUser = await this.gplus.login({
  //     webClientId:
  //       '438147440662-gn72qr50sf30b37soa01l0ckpnu54nnh.apps.googleusercontent.com',
  //     offline: true,
  //     scopes: 'profile.email',
  //   });
  //   return await this.afAuth.auth.signInWithCredential(
  //     auth.GoogleAuthProvider.credential(gplusUser.idToken)
  //   );
  // }

  async isLoggedIn() {
    const uid = await this.uid();

    const isLoggedIn = !!uid ? true : false;

    return isLoggedIn;
  }

  redirectUser() {
    const user$ = this.user$.subscribe((user) => {
      if (user && user.role === 'admin') {
        this.router.navigateByUrl('/admin');
      }
      if ((user && user.role === '') || (user && !user.role)) {
        this.router.navigateByUrl('/tutorial');
      }
      if (
        user &&
        (user.role === 'medical_personnel' || user.role === 'volunteer_maker')
      ) {
        this.router.navigateByUrl('/home');
      }

      if (user && user.role === 'jagacarepackage') {
        this.router.navigateByUrl('/jagacarepackage');
      }
    });

    setTimeout(() => {
      user$.unsubscribe();
    }, 2000);
  }
}
